import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabase/supabaseClient'
import { updateUserBet } from '../services/userDataService'
import {
  findGameForBet, settleBet, isSettleable,
  legsOf, settleMulti, settleableCandidates, fixtureUnits
} from './settlement'

// ============================================================================
// Auto-settlement of pending bets against the results feed.
//
// Runs only while the tracker is open and only when there is something to
// resolve: no pending bets in a covered sport means no requests at all.
//
// Two sources, in order:
//   1. results-proxy — the live feed, covers anything from the last 7 days
//   2. game_results  — our cache, covers games the feed has since dropped
//
// A single resolves against one fixture. A multi resolves leg by leg, each leg
// finding its own game: it goes lost the moment any one leg loses, even with
// legs still days away, and won only once every leg has.
//
// Nothing here settles a bet it is unsure about. See settlement.js.
// ============================================================================

// Sports whose results the API actually serves. Adding one is a line here plus
// its entry in SPORT_SCORE_FIELD (settlement.js); player props need nothing.
//
// BOTH edits are required and they fail differently: without this line a sport
// is never polled at all, and without the score field its team markets resolve
// to null and stay pending forever while its player props quietly settle.
const SUPPORTED_SPORTS = ['AFL', 'MLB', 'NRL', 'WNBA', 'NFL', 'NBA']

const POLL_INTERVAL_MS = 60 * 1000

// The feed keeps 7 days. Bets older than that can only come from our cache, so
// polling the API for them is wasted; they take the cache path instead.
const FEED_RETENTION_DAYS = 7

const asMs = (naiveUtc) => new Date(`${naiveUtc}Z`).getTime()

/** Pending bets in a covered sport. The rules live in settlement.js, tested. */
const candidatesIn = (bets) => settleableCandidates(bets, SUPPORTED_SPORTS)

/** Split by whether the feed can still answer for them. */
function splitByAge(units) {
  const cutoff = Date.now() - FEED_RETENTION_DAYS * 24 * 3600 * 1000
  const fresh = []
  const stale = []
  for (const unit of units) {
    (asMs(unit.commence_time) >= cutoff ? fresh : stale).push(unit)
  }
  return { fresh, stale }
}

async function fetchFromProxy(sport, needPlayers) {
  const { data, error } = await supabase.functions.invoke('results-proxy', {
    body: {
      sport,
      status: 'all',                      // live games too, for on-screen scores
      days: FEED_RETENTION_DAYS,
      // Player stats are the bulk of the payload, so only ask when a pending
      // bet actually needs them.
      include: needPlayers ? 'scores,players' : 'scores'
    }
  })
  if (error) throw error
  return data?.games ?? []
}

/** Cached games for bets the feed no longer covers. */
async function fetchFromCache(sport, bets) {
  if (bets.length === 0) return []

  // Widen by the matching window so a game whose feed start time differs
  // slightly from the bet's is still returned.
  const times = bets.map(b => asMs(b.commence_time)).filter(Number.isFinite)
  if (times.length === 0) return []
  const pad = 6 * 3600 * 1000

  const { data, error } = await supabase
    .from('game_results')
    .select('game_id, home_team, away_team, commence_time, status, concluded_at, score, players, updated_at')
    .eq('sport', sport)
    .gte('commence_time', new Date(Math.min(...times) - pad).toISOString())
    .lte('commence_time', new Date(Math.max(...times) + pad).toISOString())

  if (error) throw error

  // Reshape into the feed's { game, score, players } form so one matcher and
  // one settler serve both sources.
  return (data ?? []).map(row => ({
    game: {
      game_id: row.game_id,
      home_team: row.home_team,
      away_team: row.away_team,
      // Postgres returns ISO with an offset; settlement.js appends "Z" to the
      // feed's naive format, so strip back to naive UTC for a single shape.
      commence_time: new Date(row.commence_time).toISOString().slice(0, 19),
      status: row.status,
      concluded_at: row.concluded_at
        ? new Date(row.concluded_at).toISOString().slice(0, 19)
        : null,
      // liveSignature keys off last_update to decide whether anything moved.
      // Without this every cached game hashes to the same string forever, so a
      // score written to game_results never reaches the screen until a reload.
      last_update: row.updated_at ?? null
    },
    score: row.score,
    players: row.players
  }))
}

/**
 * Cheap identity for a set of matched games: which bets matched, and how fresh
 * each fixture is. Enough to tell "the score moved" from "nothing changed".
 */
function liveSignature(matched) {
  // A single stores one entry, a multi stores one per leg, so both shapes have
  // to hash or a multi's scores would never reach the screen.
  const stamp = (m) => (m?.legEntries
    ? m.legEntries.map(e => e?.game?.last_update ?? '-').join(',')
    : (m?.game?.last_update ?? ''))

  return Object.keys(matched)
    .sort()
    .map(id => `${id}:${stamp(matched[id])}`)
    .join('|')
}

/**
 * @param bets       every bet currently loaded in the tracker
 * @param onSettled  called with [{ id, status, actual_profit, settled_at }]
 */
export function useAutoSettle(bets, onSettled) {
  const [liveGames, setLiveGames] = useState({})   // betId -> matched game entry
  const running = useRef(false)                     // guard against overlapping polls
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled

  // The poll interval captures runPass once, so reading `bets` from the closure
  // would pin it to whatever it was when the timer started. A bet edited since
  // then would be settled from the stale copy and the user's edit overwritten,
  // because the writeback sends the whole bet_data blob. Always read live.
  const betsRef = useRef(bets)
  betsRef.current = bets

  const candidates = candidatesIn(bets ?? [])
  // Primitive key so the effect below re-runs when the set of bets we care
  // about changes, without re-running on every unrelated tracker render.
  const candidateKey = candidates.map(b => b.id).sort().join(',')

  const runPass = useCallback(async () => {
    if (running.current) return
    const currentBets = betsRef.current ?? []
    const pending = candidatesIn(currentBets)
    if (pending.length === 0) return

    running.current = true
    try {
      const settled = []
      const matched = {}

      // Fetch per sport, keyed so a multi whose legs span sports can be handed
      // every game it might need. A single's legs are just itself.
      const now = Date.now()
      const units = pending.flatMap(bet => fixtureUnits(bet, now))
      const gamesBySport = {}

      for (const sport of [...new Set(units.map(u => u.sport))]) {
        const forSport = units.filter(u => u.sport === sport)
        const { fresh, stale } = splitByAge(forSport)
        const needPlayers = forSport.some(u => u.player_name)

        let games = []
        if (fresh.length > 0) {
          try {
            games = await fetchFromProxy(sport, needPlayers)
          } catch (err) {
            console.error(`results-proxy failed for ${sport}:`, err.message)
          }
        }
        if (stale.length > 0) {
          try {
            games = games.concat(await fetchFromCache(sport, stale))
          } catch (err) {
            console.error(`game_results read failed for ${sport}:`, err.message)
          }
        }
        gamesBySport[sport] = games
      }

      for (const bet of pending) {
        const legs = legsOf(bet)

        if (legs) {
          // settleMulti matches each leg to its own fixture, so it gets the
          // games for every sport the legs sit in.
          const games = [...new Set(legs.map(l => l.sport))]
            .flatMap(sport => gamesBySport[sport] ?? [])
          if (games.length === 0) continue

          // A multi has one fixture PER LEG, so it cannot be stored the way a
          // single is. Keep them index-aligned with bet.legs and let the row
          // render a meter for each; liveLegs() reads this shape.
          matched[bet.id] = { legEntries: legs.map(leg => findGameForBet(leg, games)) }

          const verdict = settleMulti(bet, games)
          if (verdict) settled.push({ id: bet.id, ...verdict })
          continue
        }

        const games = gamesBySport[bet.sport] ?? []
        if (games.length === 0) continue

        const entry = findGameForBet(bet, games)
        if (!entry) continue          // no fixture matched, stays manual

        matched[bet.id] = entry

        if (!isSettleable(entry.game)) continue   // live, or inside the delay
        const verdict = settleBet(bet, entry)
        if (!verdict) continue                    // push or unhandled market

        settled.push({ id: bet.id, ...verdict })
      }

      // Only publish when something actually moved. Without this, every poll
      // hands React a new object and re-renders the whole tracker each minute
      // even when no score has changed.
      setLiveGames(prev => (liveSignature(prev) === liveSignature(matched) ? prev : matched))

      if (settled.length > 0) {
        const settledAt = new Date().toISOString()
        const applied = []

        for (const result of settled) {
          const original = currentBets.find(b => b.id === result.id)
          if (!original) continue

          // updateUserBet replaces the whole bet_data blob, so send the original
          // with the verdict layered on rather than the verdict alone.
          const { id, ...betData } = original
          const updated = {
            ...betData,
            status: result.status,
            actual_profit: result.actual_profit,
            settled_at: settledAt,
            settled_by: 'auto'   // distinguishes these from hand-settled bets
          }

          const { error } = await updateUserBet(result.id, updated)
          if (error) {
            console.error('Failed to save settled bet:', error)
            continue
          }
          applied.push({ id: result.id, ...updated })
        }

        if (applied.length > 0) onSettledRef.current?.(applied)
      }
    } finally {
      running.current = false
    }
    // No dependency on `bets`: it is read through betsRef so this callback stays
    // stable and the interval below is never torn down and restarted.
  }, [])

  useEffect(() => {
    if (candidateKey === '') {
      setLiveGames({})
      return            // nothing pending, so no timer and no requests
    }
    runPass()
    const timer = setInterval(runPass, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
    // runPass is stable (empty deps, reads bets through a ref), so it is safe to
    // leave out and the interval survives unrelated tracker re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateKey])

  return { liveGames }
}
